import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  VideoIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  ClockIcon,
  LinkIcon,
  PencilIcon,
  BellIcon,
  BanknoteIcon,
  MessageCircleIcon,
  CheckCircle,
  SendIcon,
  TrashIcon,
  MoreVerticalIcon,
  XIcon,
  PaperclipIcon,
  Pill,
} from 'lucide-react';
import 'react-quill/dist/quill.snow.css';
import { useSingleAppointment } from '../hooks/useSingleAppointment';
import { useAppointmentNotes } from '../hooks/useAppointmentNotes';
import { useCreateAppointmentGlobal, useUpdateAppointmentGlobal, useDeleteAppointmentGlobal } from '../hooks/useAllAppointmentsMutations';
import { AppointmentFormModal, type AppointmentFormData } from '../components/appointments/AppointmentFormModal';
import { ReminderFormModal } from '../components/reminders/ReminderFormModal';
import { PaymentFormModal } from '../components/PaymentFormModal';
import { CancelAppointmentModal } from '../components/appointments/CancelAppointmentModal';
import SendAppointmentMessageModal from '../components/appointments/SendAppointmentMessageModal';
import { GoogleCalendarLinkService } from '../../lib/services/google-calendar-link.service';
import { useAuth } from '../contexts/AuthContext';
import { useClients } from '../hooks/useClients';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../lib/api';
import { createClient } from '../../lib/supabase/client';
import { buildNoteFilePath, uploadNoteFile } from '../../lib/storage/note-files';
import type { NoteRecord } from '../hooks/useNotes';
import { FileUpload } from '../components/FileUpload';
import { RichTextEditor } from '../components/RichTextEditor';
import { AssignSupplementModal } from '../components/supplements/AssignSupplementModal';
import { useClientPayments, type PaymentRecord } from '../hooks/usePayments';
import { useClientSupplements } from '../hooks/useInventory';

function formatDateTime(iso: string) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return { dateLabel: iso, timeLabel: '' };
  }
  return {
    dateLabel: date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    timeLabel: date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }),
  };
}

function calculateDuration(starts_at: string, ends_at: string): string {
  const start = new Date(starts_at);
  const end = new Date(ends_at);
  const diffMs = end.getTime() - start.getTime();
  const diffMins = Math.round(diffMs / 60000);

  if (diffMins < 60) {
    return `${diffMins} min`;
  }
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function getMeetingTypeIcon(type: 'in_person' | 'video' | 'phone') {
  switch (type) {
    case 'video':
      return <VideoIcon className="w-6 h-6 text-blue-600" />;
    case 'phone':
      return <PhoneIcon className="w-6 h-6 text-purple-600" />;
    case 'in_person':
      return <MapPinIcon className="w-6 h-6 text-green-600" />;
  }
}

function getMeetingTypeLabel(type: 'in_person' | 'video' | 'phone') {
  switch (type) {
    case 'video':
      return 'Video Call';
    case 'phone':
      return 'Phone Call';
    case 'in_person':
      return 'In-Person';
  }
}

function getStatusColor(status: string) {
  switch (status) {
    case 'scheduled':
      return 'bg-blue-100 text-blue-700';
    case 'completed':
      return 'bg-green-100 text-green-700';
    case 'cancelled':
      return 'bg-red-100 text-red-700';
    case 'no_show':
      return 'bg-orange-100 text-orange-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
}

function getStatusLabel(status: string) {
  switch (status) {
    case 'scheduled':
      return 'Scheduled';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    case 'no_show':
      return 'No Show';
    default:
      return status;
  }
}

function getClientStatusColor(status: string) {
  switch (status) {
    case 'active':
      return 'bg-[#5B8E87] text-white';
    case 'lead':
      return 'bg-[#C9A870] text-white';
    case 'past':
      return 'bg-gray-400 text-white';
    default:
      return 'bg-gray-200 text-gray-600';
  }
}

function getClientStatusLabel(status: string) {
  switch (status) {
    case 'active':
      return 'Active';
    case 'lead':
      return 'Lead';
    case 'past':
      return 'Inactive';
    default:
      return status;
  }
}

// Strip HTML tags for plain text display
function stripHtml(html: string): string {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
}

export function AppointmentDetail() {
  const { appointmentId } = useParams<{ appointmentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();
  const queryClient = useQueryClient();

  // Fetch appointment and notes
  const { data: appointment, isLoading, error } = useSingleAppointment(appointmentId || '');
  const { data: notes = [], refetch: refetchNotes } = useAppointmentNotes(
    appointment?.client_id || '',
    appointmentId || ''
  );
  const clientsQuery = useClients();
  const clients = clientsQuery.data || [];

  // Mutations
  const createAppointmentMutation = useCreateAppointmentGlobal();
  const updateAppointmentMutation = useUpdateAppointmentGlobal();
  const deleteAppointmentMutation = useDeleteAppointmentGlobal();

  // Create reminder mutation
  const createReminderMutation = useMutation({
    mutationFn: async ({
      clientId,
      payload
    }: {
      clientId: string;
      payload: { title: string; description: string; due_at: string }
    }) => {
      if (!session?.access_token) {
        throw new Error('Session expired. Please log in again.');
      }
      const response = await apiRequest(
        `/api/clients/${clientId}/reminders`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
        session.access_token
      );
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reminders'] });
      setToast({ message: 'Reminder created successfully', type: 'success' });
      setShowReminderModal(false);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error ? error.message : 'Failed to create reminder';
      setToast({ message, type: 'error' });
    },
  });

  // Generate default note title based on appointment context
  const generateDefaultNoteTitle = (appointment: typeof appointment): string => {
    if (!appointment) return '';
    const date = new Date(appointment.starts_at);
    const formattedDate = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${appointment.title} - Session Notes (${formattedDate})`;
  };

  // Note form state
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [noteTags, setNoteTags] = useState('session-notes');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<string>('');

  // Pre-fill note title when appointment loads
  useEffect(() => {
    if (appointment && !noteTitle) {
      setNoteTitle(generateDefaultNoteTitle(appointment));
    }
  }, [appointment]); // Only depend on appointment, not noteTitle to avoid loops

  // Create note mutation
  const createNoteMutation = useMutation({
    mutationFn: async () => {
      if (!session?.access_token || !session?.user?.id || !appointment) {
        throw new Error('You must be logged in to save notes');
      }
      if (!noteTitle.trim() || !noteContent.trim()) {
        throw new Error('Title and note content are required');
      }

      const supabase = createClient();
      const fileUrls: string[] = [];
      const fileNames: string[] = [];
      const fileTypes: string[] = [];
      const fileSizes: number[] = [];

      // Upload files if any
      if (selectedFiles.length > 0) {
        setUploadProgress(`Uploading ${selectedFiles.length} file(s)...`);

        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          setUploadProgress(`Uploading ${file.name} (${i + 1}/${selectedFiles.length})...`);

          const path = buildNoteFilePath({
            userId: session.user.id,
            clientId: appointment.client_id,
            originalName: file.name,
          });

          await uploadNoteFile(supabase, path, file);

          fileUrls.push(path);
          fileNames.push(file.name);
          fileTypes.push(file.type);
          fileSizes.push(file.size);
        }
      }

      setUploadProgress('Saving note...');

      const payload = {
        title: noteTitle.trim(),
        body: noteContent.trim(),
        tags: noteTags.split(',').map((tag) => tag.trim()).filter(Boolean),
        file_urls: fileUrls,
        file_names: fileNames,
        file_types: fileTypes,
        file_sizes: fileSizes,
        appointment_id: appointmentId || null,
      };

      return apiRequest<NoteRecord>(
        `/api/clients/${appointment.client_id}/notes`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        },
        session.access_token,
      );
    },
    onSuccess: () => {
      refetchNotes();
      // Reset form to default state with pre-filled title for next note
      setNoteTitle(appointment ? generateDefaultNoteTitle(appointment) : '');
      setNoteContent('');
      setNoteTags('session-notes');
      setSelectedFiles([]);
      setUploadProgress('');
      setToast({ message: 'Note added successfully', type: 'success' });
    },
    onError: (err) => {
      setUploadProgress('');
      setToast({ message: err instanceof Error ? err.message : 'Failed to save note', type: 'error' });
    },
  });

  // Modal states
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAppointmentFormModal, setShowAppointmentFormModal] = useState(false);
  const [appointmentFormMode, setAppointmentFormMode] = useState<'edit' | 'create'>('edit');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsAppMessageType, setWhatsAppMessageType] = useState<'confirmation' | 'reminder'>('confirmation');
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const [showSupplementModal, setShowSupplementModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Fetch payment data for this appointment's client
  const { data: paymentsData } = useClientPayments(appointment?.client_id || '');

  // Find payment linked to this appointment
  const appointmentPayment = paymentsData?.find(p => p.appointment_id === appointmentId);

  // Fetch supplements for this client and filter by appointment
  const { data: supplementsData } = useClientSupplements(appointment?.client_id || '');
  const appointmentSupplements = (supplementsData?.supplements || []).filter(
    (s) => s.appointment_id === appointmentId
  );

  // Toast state
  const [toast, setToast] = useState<{ message: string; type?: 'success' | 'error' } | null>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowOverflowMenu(false);
      }
    };

    if (showOverflowMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showOverflowMenu]);

  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const handleBack = () => {
    // Smart back navigation based on where we came from
    const from = (location.state as any)?.from;
    const clientId = (location.state as any)?.clientId;

    if (from === 'client' && clientId) {
      navigate(`/client/${clientId}?tab=appointments`, { replace: true });
    } else if (from === 'schedule') {
      navigate('/schedule?tab=appointments', { replace: true });
    } else if (appointment?.client_id) {
      // Default to client appointments tab if we have a client ID
      navigate(`/client/${appointment.client_id}?tab=appointments`, { replace: true });
    } else {
      // Fallback to schedule page
      navigate('/schedule?tab=appointments', { replace: true });
    }
  };

  const handleMarkCompleted = async () => {
    if (!appointment) return;
    try {
      await updateAppointmentMutation.mutateAsync({
        clientId: appointment.client_id,
        appointmentId: appointment.id,
        payload: { status: 'completed' },
      });
      setToast({ message: 'Appointment marked as completed', type: 'success' });
    } catch (error) {
      setToast({ message: 'Failed to update appointment', type: 'error' });
    }
  };

  const handleCancelConfirm = async (reason: string) => {
    if (!appointment) return;
    try {
      await updateAppointmentMutation.mutateAsync({
        clientId: appointment.client_id,
        appointmentId: appointment.id,
        payload: {
          status: 'cancelled',
          cancellation_reason: reason,
        },
      });
      setToast({ message: 'Appointment cancelled', type: 'success' });
      setShowCancelModal(false);
    } catch (error) {
      setToast({ message: 'Failed to cancel appointment', type: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!appointment || !confirm(`Delete "${appointment.title}"?`)) return;
    try {
      await deleteAppointmentMutation.mutateAsync({
        clientId: appointment.client_id,
        appointmentId: appointment.id,
      });
      setToast({ message: 'Appointment deleted', type: 'success' });
      setTimeout(() => handleBack(), 1000);
    } catch (error) {
      setToast({ message: 'Failed to delete appointment', type: 'error' });
    }
  };

  const handleAddToGoogleCalendar = () => {
    if (!appointment) return;
    const calendarUrl = GoogleCalendarLinkService.generateAddToCalendarUrl(
      appointment,
      appointment.client_name,
      appointment.client_phone,
      appointment.client_status
    );
    window.open(calendarUrl, '_blank');
    setShowOverflowMenu(false);
  };

  const handleSendWhatsApp = (type: 'confirmation' | 'reminder') => {
    setWhatsAppMessageType(type);
    setShowWhatsAppModal(true);
    setShowOverflowMenu(false);
  };

  const handleAppointmentFormSubmit = async (formData: AppointmentFormData & { clientId: string }) => {
    if (!appointment) return;
    try {
      const payload = {
        title: formData.title,
        notes: formData.notes || undefined,
        starts_at: new Date(`${formData.date}T${formData.start_time}`).toISOString(),
        ends_at: new Date(`${formData.date}T${formData.end_time}`).toISOString(),
        meeting_type: formData.meeting_type,
        meeting_link: formData.meeting_link || undefined,
      };

      if (appointmentFormMode === 'create') {
        // Creating a new appointment
        await createAppointmentMutation.mutateAsync({
          clientId: formData.clientId,
          payload,
        });
        setToast({ message: 'Appointment created successfully', type: 'success' });
      } else {
        // Editing existing appointment
        await updateAppointmentMutation.mutateAsync({
          clientId: appointment.client_id,
          appointmentId: appointment.id,
          payload,
        });
        setToast({ message: 'Appointment updated successfully', type: 'success' });
      }
      setShowAppointmentFormModal(false);
    } catch (error) {
      setToast({
        message: appointmentFormMode === 'create' ? 'Failed to create appointment' : 'Failed to update appointment',
        type: 'error'
      });
    }
  };

  const handleReminderFormSubmit = (formData: {
    title: string;
    description: string;
    date: string;
    time: string;
    clientId?: string;
  }) => {
    if (!appointment) return;

    // Combine date and time into ISO timestamp
    const dueAt = new Date(`${formData.date}T${formData.time}`).toISOString();

    const payload = {
      title: formData.title,
      description: formData.description,
      due_at: dueAt,
    };

    createReminderMutation.mutate({
      clientId: appointment.client_id,
      payload
    });
  };

  const handleSaveNote = () => {
    createNoteMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B8E87] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading appointment...</p>
        </div>
      </div>
    );
  }

  if (error || !appointment) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center max-w-md">
          <XIcon className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-[#212121] mb-2">Appointment Not Found</h3>
          <p className="text-gray-600 mb-6">This appointment may have been deleted or you don't have access to it.</p>
          <button
            onClick={handleBack}
            className="px-6 py-2.5 bg-[#5B8E87] text-white rounded-xl font-medium hover:bg-[#4F7A74] transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const startTime = formatDateTime(appointment.starts_at);
  const endTime = formatDateTime(appointment.ends_at);
  const duration = calculateDuration(appointment.starts_at, appointment.ends_at);
  const isScheduled = appointment.status === 'scheduled';

  return (
    <div className="min-h-screen bg-[#F5F5F5] pb-6">
      {/* Header with Actions */}
      <div className="bg-white shadow-sm sticky top-0 z-20">
        <div className="p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 flex-1">
            <button
              onClick={handleBack}
              className="text-gray-600 hover:text-gray-800"
            >
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-semibold text-[#212121]">Appointment Details</h1>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {isScheduled && (
              <>
                <button
                  onClick={handleMarkCompleted}
                  className="p-2 bg-[#5B8E87] text-white rounded-lg hover:bg-[#4F7A74] transition-colors"
                  title="Mark Completed"
                >
                  <CheckCircle className="w-5 h-5" />
                </button>
                <button
                  onClick={handleAddToGoogleCalendar}
                  className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  title="Add to Google Calendar"
                >
                  <CalendarIcon className="w-5 h-5" />
                </button>
              </>
            )}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowOverflowMenu(!showOverflowMenu)}
                className="p-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <MoreVerticalIcon className="w-5 h-5" />
              </button>

              {/* Overflow Menu */}
              {showOverflowMenu && (
                <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50 min-w-[200px]">
                  <button
                    onClick={() => {
                      setAppointmentFormMode('edit');
                      setShowAppointmentFormModal(true);
                      setShowOverflowMenu(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <PencilIcon className="w-4 h-4" />
                    Edit
                  </button>
                  {isScheduled && (
                    <>
                      <button
                        onClick={() => handleSendWhatsApp('confirmation')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <SendIcon className="w-4 h-4" />
                        Send Confirmation
                      </button>
                      <button
                        onClick={() => handleSendWhatsApp('reminder')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                      >
                        <SendIcon className="w-4 h-4" />
                        Send Reminder
                      </button>
                      <div className="border-t border-gray-100 my-1" />
                      <button
                        onClick={() => {
                          setShowCancelModal(true);
                          setShowOverflowMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-2"
                      >
                        <XIcon className="w-4 h-4" />
                        Cancel Appointment
                      </button>
                    </>
                  )}
                  <div className="border-t border-gray-100 my-1" />
                  <button
                    onClick={handleDelete}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4">
          <div className={`rounded-xl border px-4 py-3 text-sm shadow-lg ${
            toast.type === 'error'
              ? 'bg-red-50 text-red-700 border-red-200'
              : 'bg-green-50 text-green-700 border-green-200'
          }`}>
            {toast.message}
          </div>
        </div>
      )}

      <div className="p-4 space-y-4">
        {/* Compact Appointment Card */}
        <div className="bg-white rounded-2xl shadow-sm p-4 border border-gray-100">
          {/* Header Row */}
          <div className="flex items-start gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center flex-shrink-0">
              {getMeetingTypeIcon(appointment.meeting_type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h2 className="text-lg font-bold text-[#212121]">{appointment.title}</h2>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(appointment.status)}`}>
                  {getStatusLabel(appointment.status)}
                </span>
              </div>

              {/* Date and Time - Compact */}
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                <CalendarIcon className="w-3.5 h-3.5" />
                <span>{startTime.dateLabel}</span>
                <span className="text-gray-400">•</span>
                <ClockIcon className="w-3.5 h-3.5" />
                <span>{startTime.timeLabel} - {endTime.timeLabel}</span>
                <span className="text-gray-400 text-xs">({duration})</span>
              </div>

              {/* Client Info - Compact */}
              <button
                onClick={() => navigate(`/client/${appointment.client_id}`)}
                className="flex items-center gap-2 mb-2 hover:underline group"
              >
                <span className="text-sm font-medium text-gray-900 group-hover:text-[#7B9EAE] transition-colors">
                  {appointment.client_name}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getClientStatusColor(appointment.client_status)}`}>
                  {getClientStatusLabel(appointment.client_status)}
                </span>
              </button>

              {/* Meeting Type and Link - Compact */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-lg text-xs text-gray-700">
                  {getMeetingTypeLabel(appointment.meeting_type)}
                </span>
                {appointment.meeting_link && appointment.meeting_type === 'video' && (
                  <a
                    href={appointment.meeting_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 rounded-lg text-xs text-blue-600 hover:bg-blue-100 transition-colors"
                  >
                    <LinkIcon className="w-3 h-3" />
                    <span>Join Meeting</span>
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Notes - Only if exists */}
          {appointment.notes && (
            <div className="bg-gray-50 rounded-lg p-3 mt-3">
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{appointment.notes}</p>
            </div>
          )}

          {/* Cancellation Reason - Only if exists */}
          {appointment.status === 'cancelled' && appointment.cancellation_reason && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
              <p className="text-xs text-red-600">
                <span className="font-medium">Cancelled:</span> {appointment.cancellation_reason}
              </p>
            </div>
          )}
        </div>

        {/* Inline Note Form */}
        <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Note</h3>

          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Title *
              </label>
              <input
                type="text"
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
                placeholder="e.g., Initial Consultation - Session Notes"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                disabled={createNoteMutation.isPending}
              />
            </div>

            {/* Rich Text Content - Multi-line */}
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Note Content *
              </label>
              <RichTextEditor
                value={noteContent}
                onChange={setNoteContent}
                placeholder="Add your note here..."
                disabled={createNoteMutation.isPending}
                minHeight="300px"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Tags
              </label>
              <input
                type="text"
                value={noteTags}
                onChange={(e) => setNoteTags(e.target.value)}
                placeholder="session-notes, follow-up, action-items"
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-[#5B8E87] focus:ring-2 focus:ring-[#5B8E87] focus:ring-opacity-20 outline-none transition-all"
                disabled={createNoteMutation.isPending}
              />
              <p className="mt-1 text-xs text-gray-500">Comma-separated tags (e.g., session-notes, follow-up)</p>
            </div>

            {/* Attachments */}
            <div>
              <label className="block text-sm font-medium text-[#212121] mb-2">
                Attachments (optional)
              </label>
              <FileUpload
                onFilesChange={setSelectedFiles}
                maxFiles={5}
                initialFiles={selectedFiles}
              />
            </div>

            {uploadProgress && (
              <div className="bg-blue-50 border border-blue-200 text-blue-700 text-sm px-4 py-2 rounded-xl">
                {uploadProgress}
              </div>
            )}

            {/* Save Button */}
            <button
              onClick={handleSaveNote}
              disabled={!noteTitle.trim() || !noteContent.trim() || createNoteMutation.isPending}
              className={`w-full py-3 rounded-xl font-medium transition-colors ${
                noteTitle.trim() && noteContent.trim() && !createNoteMutation.isPending
                  ? 'bg-[#5B8E87] text-white hover:bg-[#4F7A74]'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {createNoteMutation.isPending ? 'Saving...' : 'Save Note'}
            </button>
          </div>
        </div>

        {/* Linked Notes */}
        {notes.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Linked Notes ({notes.length})</h3>
            <div className="space-y-3">
              {notes.map((note) => (
                <div
                  key={note.id}
                  className="bg-gray-50 rounded-xl p-4 border border-gray-200 hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => navigate(`/client/${appointment.client_id}/note/${note.id}`)}
                >
                  <h4 className="font-semibold text-gray-900 mb-1">{note.title}</h4>
                  <p className="text-sm text-gray-700 mb-2 whitespace-pre-wrap leading-relaxed">
                    {stripHtml(note.body)}
                  </p>
                  {note.tags && note.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {note.tags.map((tag, idx) => (
                        <span key={idx} className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions - 4 Buttons in Horizontal Row */}
        <div className="grid grid-cols-2 gap-3 pt-2">
          <button
            onClick={() => setShowReminderModal(true)}
            className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-[#C9A870] hover:bg-[#C9A870] hover:bg-opacity-5 transition-all group"
          >
            <div className="w-12 h-12 rounded-full bg-[#C9A870] bg-opacity-10 flex items-center justify-center group-hover:bg-[#C9A870] group-hover:bg-opacity-20">
              <BellIcon className="w-6 h-6 text-[#C9A870]" />
            </div>
            <span className="text-sm font-medium text-gray-700 group-hover:text-[#C9A870]">Add Reminder</span>
          </button>

          <button
            onClick={() => setShowPaymentModal(true)}
            className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-green-600 hover:bg-green-50 hover:bg-opacity-50 transition-all group"
          >
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center group-hover:bg-green-200">
              <BanknoteIcon className="w-6 h-6 text-green-600" />
            </div>
            <span className="text-sm font-medium text-gray-700 group-hover:text-green-700">Add Payment</span>
          </button>

          <button
            onClick={() => setShowSupplementModal(true)}
            className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-[#5B8E87] hover:bg-[#5B8E87] hover:bg-opacity-5 transition-all group"
          >
            <div className="w-12 h-12 rounded-full bg-[#5B8E87] bg-opacity-10 flex items-center justify-center group-hover:bg-[#5B8E87] group-hover:bg-opacity-20">
              <Pill className="w-6 h-6 text-[#5B8E87]" />
            </div>
            <span className="text-sm font-medium text-gray-700 group-hover:text-[#5B8E87]">Add Supplement</span>
          </button>

          <button
            onClick={() => {
              setAppointmentFormMode('create');
              setShowAppointmentFormModal(true);
            }}
            className="flex flex-col items-center justify-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-[#7B9EAE] hover:bg-[#7B9EAE] hover:bg-opacity-5 transition-all group"
          >
            <div className="w-12 h-12 rounded-full bg-[#7B9EAE] bg-opacity-10 flex items-center justify-center group-hover:bg-[#7B9EAE] group-hover:bg-opacity-20">
              <CalendarIcon className="w-6 h-6 text-[#7B9EAE]" />
            </div>
            <span className="text-sm font-medium text-gray-700 group-hover:text-[#7B9EAE]">Schedule Next</span>
          </button>
        </div>

        {/* Supplements Prescribed During This Appointment */}
        {appointmentSupplements.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              Supplements Prescribed ({appointmentSupplements.length})
            </h3>
            <div className="space-y-3">
              {appointmentSupplements.map((supplement) => {
                const product = supplement.product;
                if (!product) return null;

                const productDetails = [
                  product.brand,
                  product.dosage,
                  product.form,
                ].filter(Boolean).join(' • ');

                return (
                  <div
                    key={supplement.id}
                    className="bg-gray-50 rounded-xl p-4 border border-gray-200"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#5B8E87]/10 flex items-center justify-center flex-shrink-0">
                          <Pill className="w-5 h-5 text-[#5B8E87]" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{product.name}</h4>
                          {productDetails && (
                            <p className="text-xs text-gray-500 mt-0.5">{productDetails}</p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-sm text-gray-600">
                            <span>{supplement.quantity} units</span>
                            <span>×</span>
                            <span>₹{supplement.selling_price_per_unit}</span>
                            <span>=</span>
                            <span className="font-semibold text-gray-900">
                              ₹{Number(supplement.total_amount).toFixed(2)}
                            </span>
                          </div>
                          {supplement.dosage_instructions && (
                            <p className="text-xs text-gray-500 mt-2 italic">
                              {supplement.dosage_instructions}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          supplement.payment_status === 'paid'
                            ? 'bg-green-100 text-green-700'
                            : supplement.payment_status === 'partial'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {supplement.payment_status === 'paid'
                            ? 'Paid'
                            : supplement.payment_status === 'partial'
                            ? 'Partial'
                            : 'Pending'}
                        </span>
                        {supplement.duration_days && (
                          <span className="text-xs text-gray-500">
                            {supplement.duration_days} days
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              onClick={() => navigate(`/client/${appointment.client_id}?tab=supplements`)}
              className="w-full mt-3 py-2 text-sm font-medium text-[#5B8E87] hover:text-[#4F7A74] transition-colors"
            >
              View All Supplements →
            </button>
          </div>
        )}

        {/* Appointment Payment Summary */}
        {appointmentPayment && (
          <div className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Appointment Payment</h3>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-2xl font-bold text-gray-900">
                    ₹{appointmentPayment.amount.toFixed(2)}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Due: {new Date(appointmentPayment.due_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  appointmentPayment.status === 'paid'
                    ? 'bg-green-100 text-green-700'
                    : appointmentPayment.status === 'expired'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {appointmentPayment.status === 'paid' ? 'Paid' :
                   appointmentPayment.status === 'expired' ? 'Overdue' : 'Pending'}
                </span>
              </div>

              {appointmentPayment.line_items && appointmentPayment.line_items.length > 0 && (
                <div className="border-t border-gray-200 pt-3 mt-3 space-y-2">
                  <p className="text-xs font-medium text-gray-500 uppercase">Items:</p>
                  {appointmentPayment.line_items.map((item) => {
                    const lineItem = item as any;
                    const hasDiscount = lineItem.discount_amount && lineItem.discount_amount > 0;
                    const displayAmount = lineItem.net_amount ?? lineItem.gross_amount ?? (item.quantity * (item.unit_price ?? 0));

                    return (
                      <div key={item.id} className="flex items-start justify-between text-sm">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                              item.item_type === 'supplement'
                                ? 'bg-[#5B8E87]/10 text-[#5B8E87]'
                                : item.item_type === 'service'
                                ? 'bg-[#C9A870]/10 text-[#C9A870]'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {item.item_type === 'supplement' ? '💊' : item.item_type === 'service' ? 'S' : '#'}
                            </span>
                            <p className="text-gray-900">{item.description}</p>
                          </div>
                          <p className="text-xs text-gray-500 ml-7">
                            {item.quantity} × ₹{Number(item.unit_price || 0).toLocaleString('en-IN')}
                            {hasDiscount && (
                              <span className="text-red-500 ml-1">
                                (-₹{Number(lineItem.discount_amount || 0).toLocaleString('en-IN')})
                              </span>
                            )}
                          </p>
                        </div>
                        <p className="text-gray-900 font-medium">₹{displayAmount.toFixed(2)}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              <button
                onClick={() => navigate(`/client/${appointment.client_id}?tab=payments`)}
                className="w-full mt-3 py-2 text-sm font-medium text-[#5B8E87] hover:text-[#4F7A74] transition-colors"
              >
                View in Payments Tab →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showReminderModal && appointment && (
        <ReminderFormModal
          isOpen={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          onSubmit={handleReminderFormSubmit}
          editingReminder={null}
          clientId={appointment.client_id}
          isLoading={createReminderMutation.isPending}
          error={null}
        />
      )}

      {showPaymentModal && appointment && (
        <PaymentFormModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          editingPayment={null}
          onSuccess={(message) => setToast({ message, type: 'success' })}
          onError={(message) => setToast({ message, type: 'error' })}
          clientId={appointment.client_id}
        />
      )}

      {showAppointmentFormModal && (
        <AppointmentFormModal
          isOpen={showAppointmentFormModal}
          onClose={() => setShowAppointmentFormModal(false)}
          onSubmit={handleAppointmentFormSubmit}
          isSubmitting={
            appointmentFormMode === 'create'
              ? createAppointmentMutation.isPending
              : updateAppointmentMutation.isPending
          }
          editingAppointment={
            appointmentFormMode === 'edit'
              ? {
                  ...appointment,
                  client_name: appointment.client_name,
                  client_phone: appointment.client_phone,
                  client_status: appointment.client_status,
                }
              : null
          }
          clientId={appointment.client_id}
          clients={clients}
        />
      )}

      {showCancelModal && (
        <CancelAppointmentModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          onConfirm={handleCancelConfirm}
          appointmentTitle={appointment.title}
          isSubmitting={updateAppointmentMutation.isPending}
        />
      )}

      {showWhatsAppModal && (
        <SendAppointmentMessageModal
          isOpen={showWhatsAppModal}
          onClose={() => setShowWhatsAppModal(false)}
          onSuccess={() => setToast({ message: 'Message sent successfully', type: 'success' })}
          messageType={whatsAppMessageType}
          appointment={appointment}
          clientId={appointment.client_id}
        />
      )}

      {showSupplementModal && appointment && (
        <AssignSupplementModal
          clientId={appointment.client_id}
          appointmentId={appointmentId}
          onClose={() => setShowSupplementModal(false)}
          onSuccess={(message) => {
            setToast({ message, type: 'success' });
            setShowSupplementModal(false);
          }}
          onError={(message) => setToast({ message, type: 'error' })}
        />
      )}
    </div>
  );
}
